<template>
    <div class="company-card-wrapper">
        <div v-if="activeFilters.length" class="search-wrapper">
            <div class="search">
                <div class="search-input">
                    <div class="form-group">
                        <vSelect v-if="activeFilters.includes('rubrik')" v-model="selectedTags" :options="tags"
                                 label="title"
                                 :placeholder="translate(translations, 'location.filterby')"/>
                        <div
                            v-if="categoryFilterOptions.length || serviceFilterOptions.length"
                            ref="filterRef"
                            class="company-card-type-filter company-card-service-filter"
                            :class="{ 'is-open': filterOpen }"
                        >
                            <button
                                type="button"
                                class="company-card-type-filter-toggle"
                                :class="{
                                    active: filterOpen || selectedCategoryFilters.length || selectedService,
                                    'has-selection': selectedCategoryFilters.length || selectedService
                                }"
                                @click.stop="toggleFilter"
                            >
                                {{ selectedFilterLabel }}
                                <span class="company-card-type-filter-icon" aria-hidden="true"></span>
                            </button>
                            <div v-if="filterOpen" class="company-card-type-filter-menu">
                                <template v-if="categoryFilterOptions.length">
                                    <div class="company-card-type-filter-heading">Ich suche nach:</div>
                                    <div class="company-card-type-filter-options">
                                        <button
                                            v-for="filter in categoryFilterOptions"
                                            :key="filter.slug"
                                            type="button"
                                            class="company-card-type-filter-option"
                                            :class="{
                                                active: selectedCategoryFilters.includes(filter.slug),
                                                'has-submenu': categoryHasSubmenu(filter.slug)
                                            }"
                                            @click.stop="selectCategoryFilter(filter.slug)"
                                        >
                                            <span>{{ filter.label }}</span>
                                            <span
                                                v-if="categoryHasSubmenu(filter.slug)"
                                                class="company-card-type-filter-submenu-icon"
                                                aria-hidden="true"
                                            ></span>
                                        </button>
                                    </div>
                                    <div
                                        v-if="showServiceSubmenu && serviceFilterOptions.length"
                                        class="company-card-type-filter-services"
                                    >
                                        <div class="company-card-type-filter-heading">{{ serviceFilterHeading }}</div>
                                        <button
                                            v-for="filter in serviceFilterOptions"
                                            :key="filter.slug"
                                            type="button"
                                            class="company-card-type-filter-option"
                                            :class="{ active: selectedService === filter.value }"
                                            @click.stop="toggleServiceFilter(filter.value)"
                                        >
                                            {{ filter.label }}
                                        </button>
                                    </div>
                                </template>
                                <template v-else>
                                    <button
                                        v-for="filter in serviceFilterOptions"
                                        :key="filter.slug"
                                        type="button"
                                        class="company-card-type-filter-option"
                                        :class="{ active: selectedService === filter.value }"
                                        @click.stop="toggleServiceFilter(filter.value)"
                                    >
                                        {{ filter.label }}
                                    </button>
                                </template>
                            </div>
                        </div>
                        <div v-if="activeFilters.includes('zip')" class="input-group">
                            <input
                                id="map-zip-input" v-model="zip" type="text"
                                :placeholder="translate(translations, 'location.zip')"
                                @keyup.enter="getFilteredLocations"
                                @input="resetFilterIfNecessary"
                            >
                            <button v-if="zip.length" class="rest-btn" type="button" @click="resetFilter(false)">
                                x
                            </button>
                        </div>
                        <button class="btn btn-primary filter-btn" @click="getFilteredLocations">
                            <span class="icon after i-settings">{{
                                    translate(translations, 'location.filterlocations')
                                }}</span>
                        </button>
                    </div>
                    <div class="error">
                        <label v-if="errorMessage" for="map-zip-input" class="error-label">{{ errorMessage }}</label>
                    </div>
                </div>
            </div>
        </div>
        <div class="company-card-list">
            <CompanyCardEntry
                v-for="(location, index) in sortedLocations" :key="index" :location="location"
                :icon-path="iconPath" :translations="translations"
            />
        </div>
    </div>
</template>


<script>
import $ from 'jquery';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import vSelect from 'vue-select';
import { translate } from '../translate';
import CompanyCardEntry from './CompanyCardEntry.vue';
import OpenIndicator from './OpenIndicator.vue';

export default {
	name: 'CompanyCards',
	components: { CompanyCardEntry, vSelect },
	props: {
		locations: {
			type: Object,
			default: () => ({}),
		},
		iconPath: {
			type: String,
			required: true,
		},
		filterUrl: {
			type: String,
			required: true,
		},
		translations: {
			type: Object,
			required: true,
		},
		filterConfig: {
			type: Object,
			required: true,
		},
		defaultSearchZipLocation: {
			type: Number,
			default: null,
		},
	},
	setup(props) {
		vSelect.props.components.default = () => ({ OpenIndicator });
		const zip = ref('');
		const errorMessage = ref('');
		const isLoading = ref(false);
		const locationZip = ref('');
		const locationUids = Object.values(props.locations).map(
			(location) => location.uid,
		);
		const selectedService = ref('');
		const selectedTags = ref('');
		const selectedCategoryFilters = ref([]);
		const activeServiceCategory = ref('');
		const filterOpen = ref(false);
		const filterRef = ref(null);
		const activeFilters = Object.values(props.filterConfig);
		const zipFilterActive = ref(false);
		const categoryFilterDefinitions = [
			{ label: 'Entsorger', slug: 'entsorger', tagSlug: 'entsorgung' },
			{ label: 'Kanalreinigung', slug: 'kanalreinigung', tagSlug: 'kanalreinigung' },
			{ label: 'Consulting', slug: 'consulting', tagSlug: 'consulting' },
			{ label: 'Baustoffe', slug: 'baustoffe', tagSlug: 'baustoffe' },
			{ label: 'Containerdienst', slug: 'containerdienst', tagSlug: 'container' },
			{ label: 'Selbstanlieferung', slug: 'selbstanlieferung', tagSlug: 'selbstanlieferung' },
		];
		const serviceCategorySlugs = [
			'entsorger',
			'kanalreinigung',
			'consulting',
			'baustoffe',
		];
		const serviceFilterHeadings = {
			entsorger: 'Ich möchte entsorgen:',
			kanalreinigung: 'Kanalreinigung:',
			consulting: 'Consulting:',
			baustoffe: 'Baustoffe:',
		};
		const getFilterLabel = (value) => {
			if (value && typeof value === 'object') {
				return value.title || value.label || value.name || '';
			}

			return value ? value.toString() : '';
		};
		const normalizeFilterValue = (value) => value
			.toString()
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/ä/g, 'ae')
			.replace(/ö/g, 'oe')
			.replace(/ü/g, 'ue')
			.replace(/ß/g, 'ss')
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-|-$/g, '');

		const defaultLocations = ref(
			Object.values(props.locations).reduce((acc, location) => {
				acc[location.uid] = location;
				return acc;
			}, {}),
		);

		const categoryHasSubmenu = (slug) => serviceCategorySlugs.includes(slug);
		const getServiceCategories = (location, service) => {
			const serviceName = normalizeFilterValue(getFilterLabel(service));
			const assignment = (location.serviceCategoryAssignments || []).find(
				(item) => normalizeFilterValue(getFilterLabel(item.name)) === serviceName,
			);

			return assignment?.categories || [];
		};
		const categoryHasServiceAssignments = (slug) =>
			Object.values(defaultLocations.value).some(
				(location) => location.services.some(
					(service) => getServiceCategories(location, service).includes(slug),
				),
			);
		const locationHasServiceCategory = (location, slug) =>
			location.services.some(
				(service) => getServiceCategories(location, service).includes(slug),
			);
		const locationMatchesCategory = (location, filter) => {
			if (!filter) {
				return false;
			}

			if (categoryHasServiceAssignments(filter.slug)) {
				return locationHasServiceCategory(location, filter.slug);
			}

			const hasMatchingTag = location.tags.some(
				(tag) => normalizeFilterValue(tag) === filter.tagSlug,
			);

			if (hasMatchingTag || filter.slug !== 'baustoffe') {
				return hasMatchingTag;
			}

			return location.services.some(
				(service) => normalizeFilterValue(getFilterLabel(service)).includes('baustoff'),
			);
		};

		const services = Object.values(defaultLocations.value).reduce(
			(acc, location) => {
				for (const service of location.services) {
					if (!acc.includes(service)) {
						acc.push(service);
					}
				}
				return acc;
			},
			[],
		);

		const tags = Object.values(defaultLocations.value).reduce(
			(acc, location) => {
				for (const tag of location.tags) {
					if (!acc.includes(tag)) {
						acc.push(tag);
					}
				}
				return acc;
			},
			[],
		);

		const categoryFilterOptions = categoryFilterDefinitions
			.filter((filter) => Object.values(defaultLocations.value).some(
				(location) => locationMatchesCategory(location, filter),
			));
		const selectedCategoryOptions = computed(() => categoryFilterOptions.filter(
			(filter) => selectedCategoryFilters.value.includes(filter.slug),
		));
		const activeServiceCategoryOption = computed(() => categoryFilterOptions.find(
			(filter) => filter.slug === activeServiceCategory.value,
		));
		const showServiceSubmenu = computed(() =>
			categoryHasSubmenu(activeServiceCategory.value) &&
			selectedCategoryFilters.value.includes(activeServiceCategory.value),
		);
		const serviceFilterHeading = computed(() =>
			serviceFilterHeadings[activeServiceCategory.value] || 'Services:',
		);
		const serviceFilterOptions = computed(() => Object.values(defaultLocations.value)
			.filter((location) =>
				!showServiceSubmenu.value ||
				(
					categoryHasServiceAssignments(activeServiceCategory.value)
						? locationHasServiceCategory(location, activeServiceCategory.value)
						: locationMatchesCategory(location, activeServiceCategoryOption.value)
				),
			)
			.reduce(
				(acc, location) => {
					for (const service of location.services) {
						const label = getFilterLabel(service).trim();

						if (!label) {
							continue;
						}

						const slug = normalizeFilterValue(label);
						const usesServiceAssignments = categoryHasServiceAssignments(
							activeServiceCategory.value,
						);
						const hasExplicitCategoryTag = activeServiceCategoryOption.value
							? location.tags.some(
								(tag) => normalizeFilterValue(tag) ===
									activeServiceCategoryOption.value.tagSlug,
							)
							: false;

						if (
							showServiceSubmenu.value &&
							usesServiceAssignments &&
							!getServiceCategories(location, service).includes(
								activeServiceCategory.value,
							)
						) {
							continue;
						}

						if (
							activeServiceCategory.value === 'baustoffe' &&
							!usesServiceAssignments &&
							!hasExplicitCategoryTag &&
							!slug.includes('baustoff')
						) {
							continue;
						}

						if (!acc.some((filter) => filter.slug === slug)) {
							acc.push({
								label,
								slug,
								value: service,
							});
						}
					}

					return acc;
				},
				[],
			)
			.sort((firstService, secondService) => firstService.label.localeCompare(
				secondService.label,
				'de',
				{ sensitivity: 'base' },
			)));
		const selectedFilterLabel = computed(() => {
			if (selectedService.value) {
				return getFilterLabel(selectedService.value);
			}

			if (!selectedCategoryOptions.value.length) {
				return 'Filtern nach Services';
			}

			return selectedCategoryOptions.value
				.map((filter) => filter.label)
				.join(', ');
		});

		const filteredLocations = computed(() => {
			const locations = Object.values(defaultLocations.value).filter(
				(location) => {
					const serviceIncluded = selectedService.value
						? location.services.includes(selectedService.value)
						: true;
					const tagIncluded = selectedTags.value
						? location.tags.some((tag) => selectedTags.value.includes(tag))
						: true;
					const zipIncluded = zipFilterActive.value
						? location.searchZipCodes.length === 0 ||
							location.searchZipCodes.includes(zip.value)
						: true;
						const categoryIncluded = selectedCategoryOptions.value.length
							? selectedCategoryOptions.value.some(
								(filter) => locationMatchesCategory(location, filter),
							)
							: true;

					return serviceIncluded && tagIncluded && zipIncluded && categoryIncluded;
				},
			);

			if (
				zipFilterActive.value &&
				!locations.some((location) =>
					location.searchZipCodes.includes(zip.value),
				)
			) {
				locations.push(defaultLocations.value[props.defaultSearchZipLocation]);
			}

			return locations;
		});

		const sortedLocations = computed(() => {
			return Object.values(filteredLocations.value).sort((a, b) => {
				return a.distance - b.distance;
			});
		});

		const getFilteredLocations = () => {
			if (!activeFilters.includes('zip')) {
				return;
			}

			isLoading.value = true;
			errorMessage.value = '';
			$.ajax({
				url: props.filterUrl,
				data: {
					'tx_atllocations_ajaxinterface[radius]': 20000,
					'tx_atllocations_ajaxinterface[zip]': zip.value,
					'tx_atllocations_ajaxinterface[locationsToInclude]': locationUids,
					'tx_atllocations_ajaxinterface[includeDisposalLocations]': 1,
				},
				success: (data) => {
					isLoading.value = false;
					if (data.status !== 'error') {
						zipFilterActive.value = true;
						for (const locationData of Object.values(data.locations)) {
							const location = Object.values(defaultLocations.value).find(
								(loc) => loc.uid === locationData.uid,
							);
							if (location) {
								location.distance = locationData.distance;
							}
						}
					}
					if (data.status === 'error' && data.code === '404') {
						errorMessage.value = translate(
							props.translations,
							'location.zipnotfound',
						);
					}
				},
			});
		};

		const selectCategoryFilter = (filter) => {
			const isSelected = selectedCategoryFilters.value.includes(filter);

			if (isSelected) {
				selectedCategoryFilters.value = [];
				activeServiceCategory.value = '';
			} else {
				selectedCategoryFilters.value = [filter];
				activeServiceCategory.value = categoryHasSubmenu(filter) ? filter : '';
			}

			selectedService.value = '';
		};
		const toggleFilter = () => {
			filterOpen.value = !filterOpen.value;
		};
		const toggleServiceFilter = (filter) => {
			selectedService.value = selectedService.value === filter ? '' : filter;
			filterOpen.value = false;
		};

		const closeFilterOnOutsideClick = (event) => {
			if (filterRef.value && !filterRef.value.contains(event.target)) {
				filterOpen.value = false;
			}
		};

		onMounted(() => {
			document.addEventListener('click', closeFilterOnOutsideClick);
		});

		onBeforeUnmount(() => {
			document.removeEventListener('click', closeFilterOnOutsideClick);
		});

		const resetFilter = (keepValue) => {
			zip.value = keepValue ? zip.value : '';
			zipFilterActive.value = false;
			errorMessage.value = '';
			for (const location of Object.values(defaultLocations.value)) {
				location.distance = 0;
			}
		};

		const resetFilterIfNecessary = () => {
			if (zipFilterActive.value) {
				resetFilter(true);
			}
		};

		return {
			zip,
			errorMessage,
			isLoading,
			getFilteredLocations,
			sortedLocations,
			resetFilter,
			translate,
			locationZip,
			activeFilters,
			services,
			selectedService,
			selectedServices: selectedService,
			selectedTags,
			tags,
			categoryFilterOptions,
			filterOpen,
			filterRef,
			selectedFilterLabel,
			selectedCategoryFilters,
			selectCategoryFilter,
			categoryHasSubmenu,
			showServiceSubmenu,
			serviceFilterHeading,
			serviceFilterOptions,
			toggleFilter,
			toggleServiceFilter,
			resetFilterIfNecessary,
		};
	},
};
</script>
